// features/tree/components/PageTurnOverlay.tsx — the "turn to that page"
// transition played when opening a book-index item (Family Tree, Matrimony,
// Jobs, Services, …).
//
// Mounted once in PageShell (outside BookIndexScreen's own tree) so it
// survives the route change it ends in, same reasoning as
// features/home/components/BookFlipOverlay.tsx.
//
// Two flat leaves hinged on the same left edge (the book's spine), turning
// one after another — like riffling two pages forward rather than one
// two-sided card:
//   Phase 1 — leaf1 (front, on top) is a clone of BookIndexScreen's own
//     content — same technique as BookFlipOverlay's cloneCover(), so it
//     reads as that real page lifting off rather than a blank card. It
//     rotates 0deg -> -90deg, shrinking to nothing at the spine, while
//     leaf2 (underneath) — an opaque themed "inside cover" — rotates the
//     mirror motion, 90deg -> 0deg, growing to fully cover the screen as
//     leaf1 vanishes.
//   Phase 2 — navigate() swaps the route in, invisibly, behind leaf2 (now
//     fully opaque and flat). leaf2 itself then keeps turning the same
//     way leaf1 just did, 0deg -> -90deg, tilting away in the same 3D
//     perspective to reveal the destination screen (already mounted
//     underneath) — the reveal IS the second turn, not a separate flat
//     cross-fade, so the whole sequence reads as one continuous "turn two
//     pages, land on the target" motion rather than a flip that hands off
//     to an unrelated fade.
// Neither leaf ever needs its own back face shown — each only ever
// displays its own front, vanishing purely through perspective
// foreshortening as it approaches ±90°. That was a deliberate choice, not
// an accident: the more obvious "one card, two backface-visibility:hidden
// faces" version was tried first, and its back face reliably failed to
// ever paint past ~90° in live testing here (confirmed with a minimal,
// framework-free repro — a backface-visibility:hidden face nested inside
// an already-rotated 3D parent silently never composites past that point
// in this environment), which is exactly the "page disappears mid-flip"
// bug this component exists to fix. Independently-rotated leaves sidestep
// that failure mode entirely and were verified frame-by-frame (including
// this two-phase sequence) to render correctly at every angle.
//
// A short opacity fade after phase 2 is just a safety-net cleanup for any
// hairline sliver left at the very end of the foreshortening, not the
// reveal mechanism itself.
//
// Respects prefers-reduced-motion at the call site (BookIndexScreen skips
// calling this entirely and just navigates) rather than here, since this
// component has exactly one caller today — see pageTurnBus.ts.

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerPageTurnHandler, type PageTurnParams } from '../pageTurnBus';
import styles from './PageTurnOverlay.module.css';

const PHASE1_MS = 500; // leaf1 turns away as leaf2 turns in
const MOUNT_GRACE_MS = 90; // lets the destination screen mount/settle before leaf2 turns away and starts exposing it
const PHASE2_MS = 450; // leaf2 turns away, revealing the destination
const REVEAL_GRACE_MS = 60;
const FADE_MS = 120;
const FLIP_EASE = 'cubic-bezier(0.645, 0.045, 0.355, 1)';

export function PageTurnOverlay() {
  const navigate = useNavigate();
  const stageRef = useRef<HTMLDivElement>(null);
  const leaf1Ref = useRef<HTMLDivElement>(null);
  const leaf2Ref = useRef<HTMLDivElement>(null);
  const frontContentRef = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const clearTimers = () => {
      timers.current.forEach((id) => window.clearTimeout(id));
      timers.current = [];
    };

    const runPageTurn = ({ to, sourceEl }: PageTurnParams) => {
      const stage = stageRef.current;
      const leaf1 = leaf1Ref.current;
      const leaf2 = leaf2Ref.current;
      const frontContent = frontContentRef.current;
      if (!stage || !leaf1 || !leaf2 || !frontContent) return;

      clearTimers();

      // A structural clone of the real page content, standing in for it on
      // leaf1 — decorative only, so strip anything that would make it
      // independently interactive/announced.
      frontContent.innerHTML = '';
      const clone = sourceEl.cloneNode(true) as HTMLElement;
      clone.removeAttribute('id');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll<HTMLElement>('button, a, input, [tabindex]').forEach((el) => {
        el.setAttribute('tabindex', '-1');
        el.style.pointerEvents = 'none';
      });
      frontContent.appendChild(clone);

      // Reset both leaves flat/unflipped before the sequence starts, in
      // case a previous run was interrupted mid-flip.
      stage.style.transition = 'none';
      stage.style.visibility = 'visible';
      stage.style.opacity = '1';
      leaf1.style.transition = 'none';
      leaf1.style.transform = 'rotateY(0deg)';
      leaf2.style.transition = 'none';
      leaf2.style.transform = 'rotateY(90deg)';
      void stage.offsetWidth; // commit the reset before animating

      leaf1.style.transition = `transform ${PHASE1_MS}ms ${FLIP_EASE}`;
      leaf1.style.transform = 'rotateY(-90deg)';
      leaf2.style.transition = `transform ${PHASE1_MS}ms ${FLIP_EASE}`;
      leaf2.style.transform = 'rotateY(0deg)';

      // Phase 1 done: leaf2 is flat and fully opaque, hiding leaf1
      // (fully rotated away) and the real page underneath. Navigate now,
      // invisibly, while leaf2 keeps covering the screen.
      const t1 = window.setTimeout(() => {
        navigate(to);

        // A short beat before turning leaf2 away — mounting the
        // destination screen (e.g. TreeCanvasScreen's layout/canvas work)
        // can be heavy enough to jank the main thread right as navigate()
        // fires; starting the reveal turn in that same instant risked a
        // frame where leaf2's compositor-driven rotation had already
        // advanced past what the main thread had painted, flashing the
        // stale screen underneath before catching up (confirmed live).
        // This gives the new screen a moment to actually settle before
        // anything starts exposing it.
        const tMount = window.setTimeout(() => {
          leaf2.style.transition = 'none';
          leaf2.style.transform = 'rotateY(0deg)';
          void leaf2.offsetWidth; // commit before re-animating
          leaf2.style.transition = `transform ${PHASE2_MS}ms ${FLIP_EASE}`;
          leaf2.style.transform = 'rotateY(-90deg)';

          // Turn leaf2 itself the same way leaf1 just turned — the
          // reveal is a second page turning, not a flat cross-fade.
          const t2 = window.setTimeout(() => {
            stage.style.transition = `opacity ${FADE_MS}ms ease`;
            stage.style.opacity = '0';
            const t3 = window.setTimeout(() => {
              stage.style.visibility = 'hidden';
              stage.style.opacity = '';
              frontContent.innerHTML = '';
            }, FADE_MS);
            timers.current.push(t3);
          }, PHASE2_MS + REVEAL_GRACE_MS);
          timers.current.push(t2);
        }, MOUNT_GRACE_MS);
        timers.current.push(tMount);
      }, PHASE1_MS);
      timers.current.push(t1);
    };

    registerPageTurnHandler(runPageTurn);
    return () => {
      registerPageTurnHandler(null);
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    // data-page-turn-root bumps CSS specificity so this always wins over
    // PageShell's `.shell > * { position: relative; z-index: 1; }` reset —
    // see the identical comment/fix on BookFlipOverlay's data-book-flip-root
    // for why a bare class selector here isn't enough.
    <div ref={stageRef} className={styles.stage} data-page-turn-root aria-hidden="true">
      <div ref={leaf1Ref} className={styles.leaf1}>
        <div ref={frontContentRef} className={styles.leafContent} />
      </div>
      <div ref={leaf2Ref} className={styles.leaf2}>
        <svg className={styles.backEmblem} viewBox="0 0 56 56" fill="none" aria-hidden="true">
          <path d="M28 10C24 15 18 17 18 24C18 28 21 31 25 31" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M28 10C32 15 38 17 38 24C38 28 35 31 31 31" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M28 10V40" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
    </div>
  );
}
